/**
 * Icon Service (React port)
 *
 * Provides centralized icon path resolution for the AIEMD platform.
 * Maps icon names to SVG file paths from the public/assets/svg-icons directory.
 *
 * Ported 1:1 from the Angular `IconService`. The Angular service did NOT use
 * lucide — it resolved each registered icon NAME to a custom SVG file under
 * `/assets/svg-icons/` (the Figma design-system icon set). That exact name→path
 * mapping and public API (`getIconPath`, `hasIcon`, `getAllIconNames`,
 * `getIconCount`) is reproduced here so consumers keep working unchanged.
 *
 * Exposed as a singleton (`iconService`) preserving the exact public method names.
 */

/**
 * Icon mapping: icon name → relative SVG path.
 * Format: 'icon-name': 'category/filename.svg'
 */
const iconMap: Record<string, string> = {
  // ========================================================================
  // NAVIGATION & UI CONTROLS
  // ========================================================================
  sun: 'weather/sun.svg',
  moon: 'weather/moon.svg',
  menu: 'navigation/menu-01.svg',
  x: 'add-remove-delete/cancel-01.svg',
  close: 'add-remove-delete/cancel-01.svg',
  'chevron-down': 'arrows-sharp/arrow-down-01-sharp.svg',
  'chevron-up': 'arrows-sharp/arrow-up-01-sharp.svg',
  'chevron-left': 'arrows-sharp/arrow-left-01-sharp.svg',
  'chevron-right': 'arrows-sharp/arrow-right-01-sharp.svg',
  'arrow-left': 'arrows-sharp/arrow-left-02-sharp.svg',
  'arrow-right': 'arrows-sharp/arrow-right-02-sharp.svg',
  'arrow-back': 'arrows-round/arrow-turn-backward-round.svg',
  back: 'arrows-round/arrow-turn-backward-round.svg',
  'more-vertical': 'navigation/more-vertical.svg',
  'more-horizontal': 'navigation/more-horizontal.svg',
  'map-pin': 'location-and-maps/maps-location-01.svg',

  // ========================================================================
  // USER & AUTHENTICATION
  // ========================================================================
  user: 'users/user-account.svg',
  'user-plus': 'users/user-add-01.svg',
  'user-add': 'users/user-add-01.svg',
  'user-add-01': 'users/user-add-01.svg',
  'user-check': 'users/user-check-01.svg',
  'user-check-01': 'users/user-check-01.svg',
  'user-group': 'users/user-group.svg',
  'user-circle': 'users/user-circle.svg',
  users: 'users/user-multiple.svg',
  'users-group': 'users/user-multiple.svg',
  'user-multiple-02': 'users/user-multiple.svg',
  'user-heart': 'users/user-love-01.svg',
  'user-remove-02': 'users/user-remove-02.svg',
  'location-user-01': 'users/user-circle.svg',
  'log-out': 'login-and-logout/logout-01.svg',
  'logout-01': 'login-and-logout/logout-01.svg',
  'log-in': 'login-and-logout/login-01.svg',
  lock: 'security/lock.svg',
  unlock: 'security/lock-unlocked-03.svg',
  key: 'security/key-01.svg',
  shield: 'security/security-check.svg',
  'shield-check': 'check-validation/security-validation.svg',
  'shield-alert': 'security/security-check.svg',
  'shield-security': 'security/security.svg',
  'iris-scan': 'security/iris-scan.svg',
  'face-id': 'security/iris-scan.svg',
  fingerprint: 'security/finger-print.svg',
  'fingerprint-scan': 'security/finger-print-scan.svg',
  'id-verified': 'security/id-verified.svg',
  'user-shield': 'users/user-shield-01.svg',

  // Additional facial recognition and security icons
  'security-lock': 'security/security-lock.svg',
  'cancel-circle': 'add-remove-delete/cancel-circle.svg',
  'checkmark-badge-01': 'check-validation/checkmark-badge-01.svg',
  'checkmark-badge-02': 'check-validation/checkmark-badge-02.svg',
  'checkmark-badge-03': 'check-validation/checkmark-badge-03.svg',
  'checkmark-badge-04': 'check-validation/checkmark-badge-04.svg',
  'checkmark-circle-02': 'check-validation/checkmark-circle-02.svg',
  'information-square': 'alert-notification/information-square.svg',
  'information-diamond': 'alert-notification/information-diamond.svg',
  'user-id-verification': 'users/user-id-verification.svg',
  'security-check': 'security/security-check.svg',

  // ========================================================================
  // FILES & DOCUMENTS
  // ========================================================================
  file: 'files-folders/file-01.svg',
  'file-text': 'files-folders/file-01.svg',
  'file-add': 'files-folders/file-add.svg',
  'file-check': 'files-folders/file-add.svg',
  'file-download': 'files-folders/file-download.svg',
  'file-export': 'files-folders/file-export.svg',
  'file-shield': 'files-folders/file-security.svg',
  'file-cancel': 'files-folders/file-remove.svg',
  files: 'files-folders/file-02.svg',
  folder: 'files-folders/folder-01.svg',
  'folder-open': 'files-folders/folder-open.svg',
  download: 'download-and-upload/download-01.svg',
  upload: 'download-and-upload/upload-01.svg',
  save: 'files-folders/save-01.svg',

  // ========================================================================
  // COMMUNICATION
  // ========================================================================
  mail: 'communications/mail-01.svg',
  send: 'communications/send-01.svg',
  'message-square': 'communications/comment-01.svg',
  'message-circle': 'communications/message-circle-01.svg',
  'message-dots-02': 'communications/comment-01.svg',
  'message-translate': 'communications/message-translate.svg',
  comment: 'communications/comment-01.svg',
  inbox: 'communications/inbox-01.svg',
  bell: 'alert-notification/notification-02.svg',
  phone: 'communications/phone-01.svg',
  video: 'communications/video-01.svg',
  'video-camera': 'image-camera-and-video/video-01.svg',
  'chat-bot': 'artificial-intelligence/chat-bot.svg',
  megaphone: 'communications/comment-01.svg',
  'mic-01': 'media/mic-01.svg',
  'mic-02': 'media/mic-02.svg',
  'mic-off-01': 'media/mic-off-01.svg',
  'mic-off-02': 'media/mic-off-02.svg',

  // ========================================================================
  // MEDICAL & HEALTHCARE (from figam_demo clinical-tools-data.ts)
  // ========================================================================
  activity: 'medical/cardiogram-01.svg',
  heart: 'bookmark-favorite/heart-add.svg',
  'heart-check': 'bookmark-favorite/heart-add.svg',
  stethoscope: 'medical/stethoscope.svg',
  pill: 'medical/medicine-01.svg',
  pills: 'medical/medicine-02.svg',
  syringe: 'medical/syringe.svg',
  thermometer: 'medical/thermometer.svg',
  dna: 'medical/dna.svg',
  molecules: 'science-and-technology/molecules.svg',
  brain: 'medical/brain-02.svg',
  'brain-02': 'medical/brain-02.svg',
  'ai-brain-01': 'artificial-intelligence/ai-brain-01.svg',
  labs: 'medical/labs.svg',
  flask: 'medical/labs.svg',
  'flask-conical': 'medical/labs.svg',
  'test-tube': 'medical/labs.svg',
  mortar: 'medical/mortar.svg',
  microscope: 'medical/labs.svg',
  bandage: 'medical/bandage.svg',
  scissors: 'edit-formatting/scissor.svg',
  hospital: 'medical/hospital-01.svg',
  'hospital-bed': 'medical/hospital-bed-01.svg',
  virus: 'medical/dna.svg',
  'smartwatch-01': 'devices/airpod-01.svg',

  // ========================================================================
  // ANALYTICS & CHARTS
  // ========================================================================
  'chart-line': 'business-and-finance/chart-line-data-01.svg',
  'chart-line-data-02': 'business-and-finance/chart-line-data-02.svg',
  'chart-line-data-03': 'business-and-finance/chart-line-data-03.svg',
  'chart-line-up-01': 'business-and-finance/chart-increase.svg',
  'chart-line-up-02': 'business-and-finance/chart-increase.svg',
  'analytics-02': 'business-and-finance/chart-evaluation.svg',
  'chart-evaluation': 'business-and-finance/chart-evaluation.svg',
  'bar-chart': 'business-and-finance/chart-column.svg',
  'pie-chart': 'business-and-finance/chart-breakout-circle.svg',
  'trending-up': 'business-and-finance/chart-increase.svg',
  'chart-increase': 'business-and-finance/chart-increase.svg',
  'trending-down': 'business-and-finance/chart-decrease.svg',
  'chart-decrease': 'business-and-finance/chart-decrease.svg',
  'activity-chart': 'business-and-finance/chart-line-data-01.svg',
  'chart-network': 'business-and-finance/chart-line-data-01.svg',
  'chart-scatter-02': 'business-and-finance/chart-line-data-01.svg',
  'chart-comparison': 'business-and-finance/chart-evaluation.svg',
  'dashboard-01': 'dashboard/dashboard-square-01.svg',
  'dashboard-02': 'dashboard/dashboard-square-02.svg',
  'dashboard-03': 'dashboard/dashboard-square-03.svg',

  // ========================================================================
  // CALENDAR & TIME
  // ========================================================================
  calendar: 'date-and-time/calendar-01.svg',
  'calendar-03': 'date-and-time/calendar-03.svg',
  'calendar-check-01': 'date-and-time/calendar-01.svg',
  'calendar-setting-01': 'date-and-time/calendar-01.svg',
  clock: 'date-and-time/clock-01.svg',
  'clock-02': 'date-and-time/clock-02.svg',
  timer: 'date-and-time/timer-01.svg',
  hourglass: 'date-and-time/hourglass-01.svg',

  // ========================================================================
  // ACTIONS & CONTROLS
  // ========================================================================
  plus: 'add-remove-delete/add-01.svg',
  minus: 'add-remove-delete/minus-01.svg',
  edit: 'edit-formatting/edit-01.svg',
  trash: 'add-remove-delete/delete-01.svg',
  search: 'search/search.svg',
  'search-01': 'search/search.svg',
  filter: 'filter-sorting/filter-horizontal.svg',
  refresh: 'arrows-round/arrow-reload-horizontal-round.svg',
  settings: 'dashboard/dashboard-circle-settings.svg',
  sliders: 'settings/sliders-horizontal-01.svg',
  check: 'note-and-task/task-done-01.svg',
  'check-circle': 'check-validation/checkmark-circle-01.svg',
  'x-circle': 'add-remove-delete/cancel-circle.svg',
  'clipboard-check': 'check-validation/checkmark-circle-01.svg',
  'clipboard-list': 'files-folders/file-01.svg',
  'task-done-01': 'note-and-task/task-done-01.svg',
  'loading-01': 'mouse-and-courses/loading-01.svg',

  // ========================================================================
  // ALERTS & STATUS
  // ========================================================================
  'alert-triangle': 'alert-notification/alert-triangle.svg',
  'alert-circle': 'alert-notification/alert-circle.svg',
  'alert-01': 'alert-notification/alert-01.svg',
  'alert-diamond': 'alert-notification/alert-diamond.svg',
  info: 'alert-notification/information-circle.svg',
  'help-circle': 'alert-notification/help-circle.svg',
  'check-square': 'check-validation/checkmark-square-01.svg',

  // ========================================================================
  // CLINICAL TOOLS (from figam_demo)
  // ========================================================================
  'arrow-reload-horizontal-round': 'arrows-round/arrow-reload-horizontal-round.svg',
  'curvy-right-direction-round': 'arrows-round/curvy-right-direction-round.svg',
  'bubble-chat-question': 'communications/bubble-chat-question.svg',
  'analysis-text-link': 'business-and-finance/analysis-text-link.svg',
  algorithm: 'artificial-intelligence/algorithm.svg',

  // ========================================================================
  // DASHBOARD & WIDGETS
  // ========================================================================
  home: 'building-landmark-and-places/home-01.svg',
  grid: 'dashboard/grid-01.svg',
  layout: 'dashboard/layout-01.svg',
  sidebar: 'dashboard/sidebar-01.svg',
  maximize: 'arrows-sharp/maximize-01.svg',
  minimize: 'arrows-sharp/minimize-01.svg',

  // ========================================================================
  // BUSINESS & FINANCE
  // ========================================================================
  'credit-card': 'business-and-finance/credit-card.svg',
  card: 'business-and-finance/credit-card.svg',
  'dollar-sign': 'business-and-finance/dollar-circle.svg',
  'dollar-circle': 'business-and-finance/dollar-circle.svg',
  'dollar-01': 'business-and-finance/dollar-01.svg',
  calculator: 'business-and-finance/calculator.svg',
  certificate: 'award-reward/certificate.svg',
  briefcase: 'business-and-finance/briefcase-01.svg',
  building: 'building-landmark-and-places/building-01.svg',
  'building-05': 'building-landmark-and-places/building-05.svg',
  percentage: 'business-and-finance/chart-evaluation.svg',
  balance: 'business-and-finance/chart-evaluation.svg',
  scales: 'business-and-finance/chart-evaluation.svg',

  // ========================================================================
  // ENERGY & ACTIONS
  // ========================================================================
  zap: 'image-camera-and-video/flash.svg',
  flash: 'image-camera-and-video/flash.svg',
  lightning: 'image-camera-and-video/flash.svg',

  // ========================================================================
  // DEVICES & TECHNOLOGY
  // ========================================================================
  computer: 'devices/computer.svg',
  laptop: 'devices/laptop-01.svg',
  'laptop-02': 'devices/laptop.svg',
  monitor: 'devices/computer.svg',
  'battery-low': 'devices/battery-low.svg',

  // ========================================================================
  // BRAND LOGOS
  // ========================================================================
  github: 'brand-logo/github.svg',

  // ========================================================================
  // DATA & DATABASE
  // ========================================================================
  database: 'files-folders/folder-01.svg',
  'database-export': 'files-folders/file-export.svg',
  'database-sync': 'arrows-round/arrow-reload-horizontal-round.svg',
  api: 'files-folders/file-01.svg',
  code: 'edit-formatting/edit-01.svg',
  connection: 'arrows-round/arrow-reload-horizontal-round.svg',
  'connection-point': 'arrows-round/arrow-reload-horizontal-round.svg',
  blockchain: 'crypto/blockchain-01.svg',
  'exchange-01': 'crypto/exchange-01.svg',

  // ========================================================================
  // NAVIGATION & ROUTES
  // ========================================================================
  navigation: 'navigation/navigation-01.svg',
  'route-01': 'arrows-round/arrow-turn-forward-round.svg',
  'route-02': 'arrows-round/arrow-turn-forward-round.svg',

  // ========================================================================
  // EDUCATION & KNOWLEDGE
  // ========================================================================
  'book-open': 'education/book-open-01.svg',
  'book-open-02': 'education/book-open-02.svg',
  'graduation-cap': 'education/book-open-01.svg',
  'lightbulb-01': 'education/book-open-01.svg',

  // ========================================================================
  // AWARDS & ACHIEVEMENTS
  // ========================================================================
  'medal-01': 'award-reward/medal-01.svg',
  trophy: 'award-reward/award-01.svg',
  award: 'award-reward/award-01.svg',
  target: 'award-reward/medal-01.svg',

  // ========================================================================
  // GESTURES & ACTIONS
  // ========================================================================
  'hand-prayer': 'hand-gestures/hand-prayer.svg',
  'hand-holding-heart': 'hand-gestures/hand-prayer.svg',

  // ========================================================================
  // ENTERTAINMENT & LIFESTYLE
  // ========================================================================
  'game-controller-01': 'game-and-sports/game-controller-01.svg',
  yoga: 'medical/cardiogram-01.svg',

  // ========================================================================
  // LEGAL & COMPLIANCE
  // ========================================================================
  gavel: 'business-and-finance/briefcase-01.svg',

  // ========================================================================
  // SHOPPING & COMMERCE
  // ========================================================================
  package: 'e-commerce/package.svg',

  // ========================================================================
  // COMMUNICATION & MARKETING
  // ========================================================================
  // 'megaphone' moved to COMMUNICATION section above

  // ========================================================================
  // FAMILY & RELATIONSHIPS
  // ========================================================================
  family: 'users/user-multiple.svg',

  // ========================================================================
  // ARTIFICIAL INTELLIGENCE
  // ========================================================================
  'artificial-intelligence-01': 'artificial-intelligence/artificial-intelligence-01.svg',

  // ========================================================================
  // WIFI & NETWORK
  // ========================================================================
  'wifi-full-signal': 'wifi-and-signal/wifi-full-signal.svg',
  'wifi-off-01': 'wifi-and-signal/wifi-off-01.svg',
  'wifi-off-02': 'wifi-and-signal/wifi-off-02.svg',
  'wifi-low-signal': 'wifi-and-signal/wifi-low-signal.svg',
  'wifi-medium-signal': 'wifi-and-signal/wifi-medium-signal.svg',
  'wifi-no-signal': 'wifi-and-signal/wifi-no-signal.svg',
  'wifi-01': 'wifi-and-signal/wifi-01.svg',
  'wifi-02': 'wifi-and-signal/wifi-02.svg',
  'wifi-connected-01': 'wifi-and-signal/wifi-connected-01.svg',
  'wifi-disconnected-01': 'wifi-and-signal/wifi-disconnected-01.svg',
  'signal-full-01': 'wifi-and-signal/signal-full-01.svg',
  'signal-low-01': 'wifi-and-signal/signal-low-01.svg',
  'signal-medium-01': 'wifi-and-signal/signal-medium-01.svg',
  'no-internet': 'wifi-and-signal/no-internet.svg',

  // ========================================================================
  // LAYERS & GROUPING
  // ========================================================================
  'layers-01': 'edit-formatting/layers-01.svg',
  'layers-02': 'edit-formatting/layers-02.svg',
  'layer-add': 'edit-formatting/layer-add.svg',
  'group-layers': 'edit-formatting/group-layers.svg',

  // ========================================================================
  // MEDIA CONTROLS
  // ========================================================================
  play: 'media/play.svg',
  pause: 'media/pause.svg',
  stop: 'media/stop.svg',
  'play-circle': 'image-camera-and-video/play-circle.svg',
  'play-square': 'image-camera-and-video/play-square.svg',

  // ========================================================================
  // RELOAD & REFRESH
  // ========================================================================
  'circle-arrow-reload-01-round': 'arrows-round/circle-arrow-reload-01-round.svg',
  'circle-arrow-reload-02-round': 'arrows-round/circle-arrow-reload-02-round.svg',
  'square-arrow-reload-01-round': 'arrows-round/square-arrow-reload-01-round.svg',
  'arrow-reload-vertical-round': 'arrows-round/arrow-reload-vertical-round.svg',

  // ========================================================================
  // DELETE & REMOVE
  // ========================================================================
  'delete-02': 'add-remove-delete/delete-02.svg',
  'delete-03': 'add-remove-delete/delete-03.svg',
  'delete-04': 'add-remove-delete/delete-04.svg',
  'delete-throw': 'add-remove-delete/delete-throw.svg',
  'delete-put-back': 'add-remove-delete/delete-put-back.svg',

  // ========================================================================
  // MISC
  // ========================================================================
  star: 'bookmark-favorite/star.svg',
  bookmark: 'bookmark-favorite/bookmark-01.svg',
  tag: 'e-commerce/tag-01.svg',
  flag: 'communications/flag-01.svg',
  eye: 'edit-formatting/view.svg',
  'eye-off': 'edit-formatting/view-off.svg',
  globe: 'education/globe.svg',
  map: 'location-and-maps/maps-01.svg',
  image: 'images-and-video/image-01.svg',
  camera: 'images-and-video/camera-01.svg',
  printer: 'devices/printer.svg',
  share: 'communications/share-01.svg',
  link: 'communications/link-01.svg',
  'external-link': 'communications/link-external-01.svg',
};

/** Fallback icon path when requested icon is not found. */
const fallbackIcon = 'search/search-02.svg';

class IconService {
  /**
   * Get the full SVG path for an icon name.
   * @param iconName - The icon name (e.g., 'user', 'calendar', 'heart')
   * @returns Full path to the SVG icon file
   */
  getIconPath(iconName: string): string {
    const iconPath = iconMap[iconName];

    if (!iconPath) {
      console.warn(`[IconService] Icon not found: "${iconName}". Using fallback icon.`);
      return `/assets/svg-icons/${fallbackIcon}`;
    }

    return `/assets/svg-icons/${iconPath}`;
  }

  /**
   * Check if an icon exists in the map.
   * @param iconName - The icon name to check
   * @returns true if icon exists, false otherwise
   */
  hasIcon(iconName: string): boolean {
    return iconName in iconMap;
  }

  /**
   * Get all available icon names.
   * @returns Array of all icon names
   */
  getAllIconNames(): string[] {
    return Object.keys(iconMap);
  }

  /**
   * Get icon count.
   * @returns Total number of mapped icons
   */
  getIconCount(): number {
    return Object.keys(iconMap).length;
  }
}

/** Singleton instance, mirroring the Angular `providedIn: 'root'` service. */
export const iconService = new IconService();
export type { IconService };
